import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {DashboardDefinitionsService} from "app/services/domain/dashboard-definitions.service";
import {WidgetDataService} from "app/services/domain/widget-data.service";
import * as Models from "app/services/proxy/model/models";
import {findQueryOperatorByType} from "app/shared/query-group/query-operator-query-node";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";

@Injectable()
export class DashboardDefinitionVersionsService extends SharedSvc.BaseDefinitionVersionService<Models.DashboardConfiguration, Models.DashboardDefinitionVersion, Models.DashboardDefinition, DashboardDefinitionVersionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.DashboardDefinitionVersion, DashboardDefinitionVersionExtended.newInstance);
    }

    //--//

    async makeHead(id: string): Promise<DashboardDefinitionVersionExtended>
    {
        let model = await this.api.dashboardDefinitionVersions.makeHead(id);
        return this.wrapModel(model);
    }

    async makeRelease(id: string): Promise<DashboardDefinitionVersionExtended>
    {
        let model = await this.api.dashboardDefinitionVersions.makeRelease(id);
        return this.wrapModel(model);
    }

    async createVersion(model: Models.DashboardDefinitionVersion): Promise<Models.DashboardDefinitionVersion>
    {
        return this.api.dashboardDefinitionVersions.create(model);
    }

    //--//

    protected cachePrefix(): string { return "DashboardDefinitionVersion"; }

    protected getRaw(id: string): Promise<Models.DashboardDefinitionVersion>
    {
        return this.api.dashboardDefinitionVersions.get(id);
    }

    protected getBatchRaw(ids: string[]): Promise<Models.DashboardDefinitionVersion[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => DashboardDefinitionVersionExtended.newIdentity(id))});
        return this.api.dashboardDefinitionVersions.batch(request);
    }

    protected getBatchRawSize(): number
    {
        return 20;
    }

    protected canBatch(): boolean
    {
        return true;
    }
}

export class DashboardDefinitionVersionExtended extends SharedSvc.ExtendedDefinitionVersionModel<Models.DashboardConfiguration, Models.DashboardDefinitionVersion, Models.DashboardDefinition>
{
    public static readonly WILDCARD_TENANT_ID = "wildcardTenantId";

    static newInstance(svc: DashboardDefinitionVersionsService,
                       model: Models.DashboardDefinitionVersion): DashboardDefinitionVersionExtended
    {
        const verExt = new DashboardDefinitionVersionExtended(svc, model, "DashboardDefinitionVersion");
        verExt.fixupQueries();
        return verExt;
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "DashboardDefinitionVersion",
                                                     sysId: sysId
                                                 });
    }

    //--//

    protected definitionSvc(): DashboardDefinitionsService
    {
        return this.domain.dashboardDefinitions;
    }

    protected definitionVersionSvc(): DashboardDefinitionVersionsService
    {
        return this.domain.dashboardDefinitionVersions;
    }

    //--//

    /**
     * Get stored dashboard details
     */
    async getDetails(): Promise<Models.DashboardConfiguration>
    {
        if (!this.model.details)
        {
            let ext            = await this.domain.dashboardDefinitionVersions.getExtendedByIdentity(this.getIdentity());
            this.model.details = ext.model.details;
        }

        return this.model.details;
    }

    private fixupQueries(): void
    {
        const dashboardModels = this.model.details;
        for (const widget of dashboardModels?.widgets || [])
        {
            if (widget.config instanceof Models.GroupingWidgetConfiguration)
            {
                for (const childWidget of widget.config.widgets || [])
                {
                    this.fixupWidgetQueries(childWidget);
                }
            }
            else if (widget.config instanceof Models.TabWidgetConfiguration)
            {
                for (const tab of widget.config.tabs || [])
                {
                    if (tab.widget) this.fixupWidgetQueries(tab.widget);
                }
            }
            else
            {
                this.fixupWidgetQueries(widget);
            }
        }

        for (const savedView of dashboardModels?.savedViews || [])
        {
            const viewState = <Models.DashboardViewState>savedView.view;
            for (const widget of viewState.widgets || [])
            {
                this.fixupWidgetQueries(widget);
            }
        }

        for (const scope of dashboardModels?.filterScope || [])
        {
            if (scope.condition instanceof Models.ServiceAccountNativeDetailsQueryOperator)
            {
                scope.condition.native_details = this.fixupProviderQuery(scope.condition.native_details);
            }
        }
    }

    /**
     * Fixes widget queries by replacing wildcard tenant IDs with the last active tenant
     * and updating service account native detail selections to include cost centers.
     * This ensures compatibility with models saved before cost centers were introduced.
     */
    private fixupWidgetQueries(widget: Models.WidgetComposition | Models.WidgetViewState): void
    {
        let query: Models.QueryOperator;
        if (widget instanceof Models.WidgetComposition && widget.config instanceof Models.BaseDataWidgetConfigurationModel)
        {
            query = widget.config?.context?.query;
        }
        else if (widget instanceof Models.ChartWidgetViewState || widget instanceof Models.FilterWidgetViewState)
        {
            query = widget.context?.query;
        }

        if (!query) return;

        const tenantQuery = findQueryOperatorByType<Models.TenantQueryOperator>(query, Models.TenantQueryOperator);

        if (tenantQuery?.tenant_ids?.length === 1 && tenantQuery.tenant_ids[0] === DashboardDefinitionVersionExtended.WILDCARD_TENANT_ID)
        {
            tenantQuery.tenant_ids[0] = this.domain.users.lastActiveTenantId;
        }

        const cloudQuery = findQueryOperatorByType<Models.ServiceAccountNativeDetailsQueryOperator>(query, Models.ServiceAccountNativeDetailsQueryOperator);
        if (cloudQuery?.native_details?.length)
        {
            cloudQuery.native_details = this.fixupProviderQuery(cloudQuery.native_details);
        }
    }

    /**
     * Fixes service account native detail selections by converting them to cloud representations and back.
     * This ensures older widget queries remain compatible after cost center support was introduced.
     */
    private fixupProviderQuery(nativeDetails: Models.ServiceAccountNativeDetailsBase[] | undefined): Models.ServiceAccountNativeDetailsBase[]
    {
        if (!nativeDetails?.length) return [];
        const clouds = WidgetDataService.nativeDetailsToClouds(nativeDetails);
        return WidgetDataService.cloudsToNativeDetails(clouds);
    }
}
